/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef, useContext } from "react";
import axios from "axios";
import InputField from "components/fields/InputField";
import { Select, Button } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import AutoComplete from "react-select";
import SweetAlert2 from "react-sweetalert2";
import { Tooltip, IconButton } from "@mui/material";
import { TbRefresh } from "react-icons/tb";
import { MdOutlineContentCopy, MdInfoOutline } from "react-icons/md";
import { ImSpinner8 } from "react-icons/im";
import FeeGroupDetailModal from "./components/FeeGroupDetailModal";
import Radio from "components/radio/index";
import FeeGroupForm from "../setupWizard/components/FeeGroupForm";
import { NewFeeGroupData } from "../setupWizard/types";
import { FaAngleLeft } from "react-icons/fa";
import MerchantListModal from "../shared/MerchantListModal";

type PayoutMethod = {
  asset: string;
  crypto: boolean;
};

const initialData: {
  merchant_id: string;
  postback_url: string;
  suba_name: string;
  firstname: string;
  lastname: string;
  mobile_dialcode: string;
  mobile: string;
  email: string;
  business_name: string;
  feegroup_id: string;
  auth1: string;
  auth2: string;
  payout_methods: string[];
  is_masked: string;
} = {
  merchant_id: "",
  postback_url: "",
  suba_name: "",
  firstname: "",
  lastname: "",
  mobile_dialcode: "",
  mobile: "",
  email: "",
  business_name: "",
  feegroup_id: "",
  auth1: "",
  auth2: "",
  payout_methods: [],
  is_masked: "no",
};

const CreateAccount = () => {
  const [newAccount, setNewAccount] = useState<any>(initialData);
  const [isDialcodeLoading, setIsDialcodeLoading] = useState<boolean>(true);
  const [isFeegroupLoading, setIsFeegroupLoading] = useState<boolean>(true);
  const [isPayoutMethodLoading, setIsPayoutMethodLoading] =
    useState<boolean>(true);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isPostbackUrlValid, setIsPostbackUrlValid] = useState<boolean>(true);
  const [dialcodeList, setDialcodeList] = useState<any>(null);
  const [feegroupList, setFeegroupList] = useState<any>(null);
  const [payoutMethodList, setPayoutMethodList] = useState<PayoutMethod[]>([]);
  const [swalProps, setSwalProps] = useState<any>({});

  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const isMerchant = userRole === "merchant";
  const storedMerchantId = localStorage.getItem("merchantId");

  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState<boolean>(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);

  const dialcodeAutoCompleteRef = useRef(null);
  const [dialcodeAutoCompleteOptions, setDialcodeAutoCompleteOptions] =
    useState<any>(null);

  const feegroupAutoCompleteRef = useRef(null);
  const [feegroupAutoCompleteOptions, setFeegroupAutoCompleteOptions] =
    useState<any>(null);

  const [apiKey, setApiKey] = useState<string>("");
  const [apiSecret, setApiSecret] = useState<string>("");
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [feeGroupMode, setFeeGroupMode] = useState<"existing" | "new">(
    "existing"
  );
  const [newFeeGroup, setNewFeeGroup] = useState<NewFeeGroupData>({
    name: "",
    description: "",
    fee_data: [],
  });

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    if (id === "mobile") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setNewAccount({ ...newAccount, [id]: numericValue });
    } else {
      setNewAccount({ ...newAccount, [id]: value });
    }

    if (id === "postback_url") {
      validatePostbackUrl(value);
    }
  };

  const validatePostbackUrl = (url: string) => {
    if (url === "") {
      setIsPostbackUrlValid(true);
      return;
    }
    const urlPattern =
      /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,6})(\/[\w.-]*)*\/?(\?.*)?(#.*)?$/i;
    setIsPostbackUrlValid(urlPattern.test(url));
  };

  const generateAPIKey = () => {
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    const generatedKey = Array.from(array, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("");
    setApiKey(generatedKey);
    setNewAccount((prev: any) => ({ ...prev, auth1: generatedKey }));
  };

  const generateAPISecret = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const generatedSecret = Array.from(array, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("");
    setApiSecret(generatedSecret);
    setNewAccount((prev: any) => ({ ...prev, auth2: generatedSecret }));
  };

  const handleClickCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isMerchant && storedMerchantId) {
        setNewAccount((prev: any) => ({ ...prev, merchant_id: storedMerchantId }));
      }
      await fetchCountries();
      await fetchFeegroups();
      await fetchPayoutMethods();

      setIsDialcodeLoading(false);
      setIsFeegroupLoading(false);
    };

    fetchData();
    generateAPIKey();
    generateAPISecret();
  }, []);

  useEffect(() => {
    if (dialcodeList !== null) {
      setIsDialcodeLoading(true);
      let rebuildListArray: any = [];

      Object.entries(dialcodeList).forEach(([key, value]: [any, any]) => {
        let newObj = {
          value: key,
          label: value,
        };
        rebuildListArray.push(newObj);
      });

      setDialcodeAutoCompleteOptions(rebuildListArray);
      setIsDialcodeLoading(false);
    }
  }, [dialcodeList]);

  useEffect(() => {
    if (feegroupList !== null) {
      setIsFeegroupLoading(true);
      let rebuildListArray: any = [];
      if (feegroupList.length > 0) {
        feegroupList.forEach((i: any) => {
          let newObj = {
            value: i.id,
            label: i.name,
          };
          rebuildListArray.push(newObj);
        });
      }

      setFeegroupAutoCompleteOptions(rebuildListArray);
      setIsFeegroupLoading(false);
    }
  }, [feegroupList]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/datasource/countries`
      );

      let countries = response?.data;
      let dialcodeList: any = [];
      if (countries.length > 0) {
        countries.forEach((c: any) => {
          if (c.dialcode !== null) {
            if (c.dialcode in dialcodeList) {
              dialcodeList[c.dialcode] =
                dialcodeList[c.dialcode] + ", " + c.country_name;
            } else {
              dialcodeList[c.dialcode] =
                "(+" + c.dialcode + ") " + c.country_name;
            }
          }
        });
      }

      const sortedDialcodeList: any = [];
      Object.keys(dialcodeList)
        .sort()
        .forEach((key: any) => {
          sortedDialcodeList[key] = dialcodeList[key];
        });

      setDialcodeList(sortedDialcodeList);
    } catch (error) {
      console.error("Error fetching countries", error);
    }
  };

  const fetchFeegroups = async () => {
    try {
      let requestUrl = `/api/feegroup/all-feegroups`;

      const response = await axios.get(
        process.env.REACT_APP_API_URL + requestUrl
      );

      const feegroupList = response.data.feegroups?.payload;

      let rebuildListArray: any = [];
      if (feegroupList.length > 0) {
        feegroupList.forEach((i: any) => {
          let newObj = {
            id: i.feegroup_id,
            name: i.description,
          };
          rebuildListArray.push(newObj);
        });
      }
      setFeegroupList(rebuildListArray);
    } catch (error) {
      console.error("Error fetching feegroups", error);
    }
  };

  const getPayoutMethods = async (): Promise<PayoutMethod[]> => {
    const response = await axios.get(
      process.env.REACT_APP_API_URL + `/api/subaccount/payout-methods`
    );
    return response.data.payoutmethods?.payload || [];
  };

  const fetchPayoutMethods = async () => {
    try {
      setIsPayoutMethodLoading(true);
      const methods = await getPayoutMethods();
      setPayoutMethodList(methods);
    } catch (error) {
      console.error("Error fetching payout assets", error);
    } finally {
      setIsPayoutMethodLoading(false);
    }
  };

  const handlePayoutMethodChange = (asset: string, checked: boolean) => {
    setNewAccount((prev: any) => {
      const currentMethods = prev.payout_methods || [];
      if (checked) {
        return { ...prev, payout_methods: [...currentMethods, asset] };
      } else {
        return {
          ...prev,
          payout_methods: currentMethods.filter((a: string) => a !== asset),
        };
      }
    });
  };

  const handleSelectAllPayoutMethods = () => {
    const currentMethods = newAccount.payout_methods || [];
    if (currentMethods.length === payoutMethodList.length) {
      setNewAccount((prev: any) => ({ ...prev, payout_methods: [] }));
    } else {
      setNewAccount((prev: any) => ({
        ...prev,
        payout_methods: payoutMethodList.map((m) => m.asset),
      }));
    }
  };

  const handleMerchantSelect = (merchantData: any) => {
    setSelectedMerchant(merchantData);
    setNewAccount((prev: any) => ({ ...prev, merchant_id: merchantData.id }));
  };

  const handleClearMerchant = () => {
    setSelectedMerchant(null);
    setNewAccount((prev: any) => ({ ...prev, merchant_id: "" }));
  };

  const handleDialcodeAutoCompleteChange = (item: any) => {
    if (item !== null) {
      setNewAccount({ ...newAccount, mobile_dialcode: item.value });
    } else {
      setNewAccount({ ...newAccount, mobile_dialcode: "" });
    }
  };

  const handleFeegroupAutoCompleteChange = (item: any) => {
    console.log("Feegroup item", item);
    if (item !== null) {
      setNewAccount({ ...newAccount, feegroup_id: item.value });
    } else {
      setNewAccount({ ...newAccount, feegroup_id: "" });
    }
  };

  const noFeegroupOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return (
      <div className="text-sm">Fee Group not found for "{inputValue}"</div>
    );
  };

  const noDialcodeOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return <div className="text-sm">Dialcode not found for "{inputValue}"</div>;
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isFeeGroupValid =
    feeGroupMode === "existing"
      ? !!newAccount.feegroup_id
      : !!newFeeGroup.name && !!newFeeGroup.description;

  const isFormValid =
    newAccount !== null
      ? newAccount.merchant_id &&
        newAccount.postback_url &&
        isPostbackUrlValid &&
        newAccount.suba_name &&
        newAccount.firstname &&
        newAccount.lastname &&
        newAccount.email &&
        isValidEmail(newAccount.email) &&
        newAccount.business_name &&
        isFeeGroupValid &&
        newAccount.auth1 &&
        newAccount.auth2
      : false;

  const handleSubmit = async () => {
    if (isFormValid) {
      const username = localStorage.getItem("username");

      try {
        setIsCreating(true);

        let feeGroupIdToUse = newAccount.feegroup_id;

        // If creating new fee group, create it first
        if (feeGroupMode === "new") {
          const feeDataPayload = newFeeGroup.fee_data.map((item) => ({
            type: item.type,
            asset: item.asset,
            asset_type: item.asset_type,
            fixed: parseFloat(item.fixed) || 0,
            percentage: parseFloat(item.percentage) || 0,
            max_amount: parseFloat(item.max_amount) || 0,
            min_amount: parseFloat(item.min_amount) || 0,
          }));

          const feeGroupResponse = await axios.post(
            process.env.REACT_APP_API_URL +
              `/api/${userRole}/feegroup/action/create-feegroup`,
            {
              name: newFeeGroup.name,
              description: newFeeGroup.description,
              fee_data: feeDataPayload,
              __with_default_feetemplate: true,
              username,
              userRole,
            }
          );

          if (feeGroupResponse.data?.status !== "ok") {
            throw new Error(
              feeGroupResponse.data?.message || "Failed to create fee group"
            );
          }

          feeGroupIdToUse = feeGroupResponse.data.result?.payload?.feegroup_id;
        }

        // Create subaccount with the fee group id
        const subaccountData = {
          ...newAccount,
          feegroup_id: feeGroupIdToUse,
          username,
          userRole,
        };

        await axios
          .post(
            process.env.REACT_APP_API_URL +
              `/api/${userRole}/subaccount/action/create-subaccount`,
            subaccountData
          )
          .then(function (response) {
            console.log("Create account response", subaccountData, response);
            if (response && response.data?.status === "ok") {
              setSwalProps({
                show: true,
                icon: "success",
                title: "Success",
                html: "Create account successfully",
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsCreating(false);
                  const subaccountDetailPath = isMerchant
                    ? `/${userRole}/subaccounts/${response.data?.result?.payload?.subaccount_id}`
                    : `/${userRole}/merchants/subaccounts/${response.data?.result?.payload?.subaccount_id}`;
                  navigate(subaccountDetailPath);
                },
              });
            } else {
              setIsCreating(false);
              setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: response.data.message,
                showConfirmButton: true,
                didClose: () => {
                  setSwalProps({});
                  setIsCreating(false);
                },
              });
            }
          })
          .catch(function (error) {
            setIsCreating(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: error.response?.data?.message || "Something went wrong",
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsCreating(false);
              },
            });
          });
      } catch (error: any) {
        setIsCreating(false);
        setSwalProps({
          show: true,
          icon: "error",
          title: "Oops!",
          html:
            error.message || "Something went wrong, please try again later.",
          showConfirmButton: true,
          didClose: () => {
            setSwalProps({});
            setIsCreating(false);
          },
        });
      }
    }
  };

  // Separate fiat and crypto methods
  const fiatMethods = payoutMethodList.filter((m) => !m.crypto);
  const cryptoMethods = payoutMethodList.filter((m) => m.crypto);

  return (
    <>
      <form>
        <div className="inter mx-auto max-w-5xl space-y-6 rounded-xl bg-white p-12 shadow-lg ring-1 ring-gray-100">
          <div>
            <Button
              type="button"
              onClick={() => navigate(isMerchant ? `/${userRole}/subaccounts` : `/${userRole}/merchants/subaccounts`)}
              isDisabled={isCreating}
              className="
                mb-1 inline-flex items-center gap-1
                text-sm font-medium text-gray-700
                transition hover:text-gray-700
                disabled:cursor-not-allowed disabled:opacity-40
                dark:text-gray-400 dark:hover:text-white
              "
            >
              <FaAngleLeft className="text-base" />
              <span>Subaccounts</span>
            </Button>

            <h2 className="text-left text-2xl font-bold text-navy-700">
              Create Account
            </h2>
          </div>

          <div className="space-y-4 border-b border-gray-200 pb-8">
            <p className="text-md font-medium tracking-normal text-gray-700">
              Provide the account information
            </p>
            {!isMerchant && (
              <div>
                <label className="text-sm font-medium text-gray-800">
                  Merchant <span className="text-red-500">*</span>
                </label>
                <Tooltip
                  title={selectedMerchant ? selectedMerchant.name : "Please Select"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isCreating && setIsMerchantModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isCreating
                        ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                        : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedMerchant ? selectedMerchant.name : "Please Select"}
                    </span>
                    {selectedMerchant && !isCreating && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearMerchant(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </Tooltip>
              </div>
            )}

            <div className="grid grid-cols-1 gap-8 gap-y-4 md:grid-cols-2">
              <div>
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="shadow-sm"
                  label="Name"
                  required={true}
                  placeholder="Enter name"
                  id="suba_name"
                  value={newAccount.suba_name}
                  type="text"
                  autoComplete="off"
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="shadow-sm"
                  label="Postback URL"
                  required={true}
                  placeholder="https://example.com"
                  id="postback_url"
                  value={newAccount.postback_url}
                  type="text"
                  autoComplete="off"
                  onChange={handleInputChange}
                  error={
                    !isPostbackUrlValid
                      ? "Invalid URL format. Please enter a valid URL, e.g. https://example.com"
                      : undefined
                  }
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 border-b border-gray-200 pb-8">
            <p className="text-md font-medium tracking-normal text-gray-700">
              Provide personal information
            </p>

            <div className="grid grid-cols-1 gap-8 gap-y-4 md:grid-cols-2">
              <div>
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="shadow-sm"
                  label="First Name"
                  required={true}
                  placeholder="Enter first name"
                  id="firstname"
                  value={newAccount.firstname}
                  type="text"
                  autoComplete="off"
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="shadow-sm"
                  label="Last Name"
                  required={true}
                  placeholder="Enter last name"
                  id="lastname"
                  value={newAccount.lastname}
                  type="text"
                  autoComplete="off"
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-800">
                  Dialcode
                </label>
                <AutoComplete
                  ref={dialcodeAutoCompleteRef}
                  isDisabled={
                    isDialcodeLoading ||
                    isCreating ||
                    dialcodeAutoCompleteOptions === null ||
                    dialcodeAutoCompleteOptions.length === 0
                  }
                  placeholder={
                    dialcodeAutoCompleteOptions === null
                      ? "Retrieving..."
                      : "Please Select"
                  }
                  options={dialcodeAutoCompleteOptions}
                  classNamePrefix="custom-autocomplete"
                  className="autocomplete-override"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                  }}
                  onChange={(item: any) =>
                    handleDialcodeAutoCompleteChange(item)
                  }
                  noOptionsMessage={noDialcodeOptionsMessageRenderer}
                  isClearable
                />
              </div>

              <div>
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="shadow-sm"
                  label="Phone"
                  placeholder="Enter mobile number"
                  id="mobile"
                  value={newAccount.mobile}
                  type="text"
                  autoComplete="off"
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="shadow-sm"
                  label="Email"
                  required={true}
                  placeholder="Enter email"
                  id="email"
                  value={newAccount.email}
                  type="email"
                  autoComplete="off"
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <InputField
                  disabled={isCreating}
                  variant="auth"
                  extra="shadow-sm"
                  label="Business Name"
                  required={true}
                  placeholder="Enter business name"
                  id="business_name"
                  value={newAccount.business_name}
                  type="text"
                  autoComplete="off"
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 border-b border-gray-200 pb-8">
            <div className="flex items-center justify-between">
              <p className="text-md font-medium tracking-normal text-gray-700">
                Provide payout assets
              </p>
              {!isPayoutMethodLoading && payoutMethodList.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAllPayoutMethods}
                  disabled={isCreating}
                  className="text-xs font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
                >
                  {(newAccount.payout_methods?.length || 0) ===
                  payoutMethodList.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              )}
            </div>
            {isPayoutMethodLoading ? (
              <div className="flex items-center justify-center py-12">
                <ImSpinner8
                  className="spinner text-primary-500"
                  style={{ fontSize: "1.8rem" }}
                />
              </div>
            ) : payoutMethodList.length === 0 ? (
              <p className="text-sm text-gray-500">
                No payout assets available
              </p>
            ) : (
              <>
                {payoutMethodList.filter((m) => !m.crypto).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-navy-700">
                      Fiat
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6">
                      {payoutMethodList
                        .filter((m) => !m.crypto)
                        .map((method) => (
                          <label
                            key={method.asset}
                            className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-all ${
                              newAccount.payout_methods?.includes(method.asset)
                                ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            } ${isCreating ? "cursor-not-allowed opacity-60" : ""}`}
                          >
                            <div className="flex items-start space-x-2">
                              <input
                                type="checkbox"
                                checked={
                                  newAccount.payout_methods?.includes(
                                    method.asset
                                  ) || false
                                }
                                onChange={(e) =>
                                  handlePayoutMethodChange(
                                    method.asset,
                                    e.target.checked
                                  )
                                }
                                disabled={isCreating}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <div className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-navy-700">
                                  {method.asset}
                                </span>
                              </div>
                            </div>
                          </label>
                        ))}
                    </div>
                  </div>
                )}
                {payoutMethodList.filter((m) => m.crypto).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-navy-700">
                      Crypto
                    </div>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-6">
                      {payoutMethodList
                        .filter((m) => m.crypto)
                        .map((method) => (
                          <label
                            key={method.asset}
                            className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-all ${
                              newAccount.payout_methods?.includes(method.asset)
                                ? "border-primary-500 bg-primary-50 ring-1 ring-primary-500"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            } ${isCreating ? "cursor-not-allowed opacity-60" : ""}`}
                          >
                            <div className="flex items-start space-x-2">
                              <input
                                type="checkbox"
                                checked={
                                  newAccount.payout_methods?.includes(
                                    method.asset
                                  ) || false
                                }
                                onChange={(e) =>
                                  handlePayoutMethodChange(
                                    method.asset,
                                    e.target.checked
                                  )
                                }
                                disabled={isCreating}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <div className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-navy-700">
                                  {method.asset}
                                </span>
                              </div>
                            </div>
                          </label>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {(newAccount.payout_methods?.length || 0) > 0 && (
              <p className="text-xs text-gray-500">
                {newAccount.payout_methods.length} payout asset
                {newAccount.payout_methods.length > 1 ? "s" : ""} selected
              </p>
            )}
          </div>

          <div className="space-y-4 border-b border-gray-200 pb-8">
            <p className="text-md font-medium tracking-normal text-gray-700">
              Provide fee information
            </p>
            <div className="space-y-4">
              <label className="text-sm font-medium text-gray-800">
                Fee Group <span className="text-red-500">*</span>
              </label>

              {/* Mode Selection - only show for non-merchant roles */}
              {!isMerchant && (
                <div className="flex items-center gap-6">
                  <label className="inline-flex items-center">
                    <Radio
                      id="fee_mode_existing"
                      name="feeGroupMode"
                      disabled={isCreating}
                      checked={feeGroupMode === "existing"}
                      onChange={() => {
                        setFeeGroupMode("existing");
                        setNewFeeGroup({
                          name: "",
                          description: "",
                          fee_data: [],
                        });
                      }}
                    />
                    <span className="ml-2 text-sm text-navy-700">
                      Select Existing
                    </span>
                  </label>
                  <label className="inline-flex items-center">
                    <Radio
                      id="fee_mode_new"
                      name="feeGroupMode"
                      disabled={isCreating}
                      checked={feeGroupMode === "new"}
                      onChange={() => {
                        setFeeGroupMode("new");
                        setNewAccount({ ...newAccount, feegroup_id: "" });
                      }}
                    />
                    <span className="ml-2 text-sm text-navy-700">Create New</span>
                  </label>
                </div>
              )}

              {/* Existing Fee Group Dropdown */}
              {feeGroupMode === "existing" && (
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <AutoComplete
                      ref={feegroupAutoCompleteRef}
                      isDisabled={
                        isFeegroupLoading ||
                        isCreating ||
                        feegroupAutoCompleteOptions === null ||
                        feegroupAutoCompleteOptions.length === 0
                      }
                      placeholder={
                        feegroupAutoCompleteOptions === null
                          ? "Retrieving..."
                          : "Please Select"
                      }
                      options={feegroupAutoCompleteOptions}
                      classNamePrefix="custom-autocomplete"
                      className="autocomplete-override"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                      }}
                      onChange={(item: any) =>
                        handleFeegroupAutoCompleteChange(item)
                      }
                      noOptionsMessage={noFeegroupOptionsMessageRenderer}
                      isClearable
                    />
                  </div>
                  <Button
                    onClick={() => setIsDetailModalOpen(true)}
                    isDisabled={
                      !newAccount.feegroup_id ||
                      isFeegroupLoading ||
                      isCreating ||
                      feegroupAutoCompleteOptions === null ||
                      feegroupAutoCompleteOptions.length === 0
                    }
                    className="btn btn-primary whitespace-nowrap text-white"
                    style={{ marginTop: "2px" }}
                  >
                    View Detail
                  </Button>
                </div>
              )}

              {/* New Fee Group Form */}
              {feeGroupMode === "new" && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <FeeGroupForm
                    value={newFeeGroup}
                    onChange={setNewFeeGroup}
                    disabled={isCreating}
                  />
                </div>
              )}
            </div>
          </div>

          {isDetailModalOpen && (
            <FeeGroupDetailModal
              isOpen={isDetailModalOpen}
              onClose={() => setIsDetailModalOpen(false)}
              feegroupId={newAccount.feegroup_id}
            />
          )}

          <div className="space-y-4 border-b border-gray-200 pb-8">
            <p className="text-md font-medium tracking-normal text-gray-700">
              Provide the account's access control credentials
            </p>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  API Key
                </label>
                <div className="flex w-full">
                  <input
                    id="auth1"
                    name="auth1"
                    type="text"
                    value={apiKey}
                    onKeyDown={(e) => e.preventDefault()}
                    className="custom-readonly caret-transparent h-10 w-full cursor-default rounded-l-md border-y border-l border-gray-300 bg-gray-50 px-4 text-sm text-gray-700 shadow-sm outline-none"
                  />
                  <div className="flex items-center rounded-r-md border border-gray-300 bg-gray-100 px-1">
                    <Tooltip title="Copy API Key" arrow placement="top">
                      <IconButton
                        id="button-copy-auth1"
                        size="small"
                        disabled={isCreating}
                        onClick={() => handleClickCopy(apiKey)}
                      >
                        <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate API Key" arrow placement="top">
                      <IconButton
                        id="button-generate-api-key"
                        size="small"
                        disabled={isCreating}
                        onClick={generateAPIKey}
                      >
                        <TbRefresh className="h-5 w-5 text-navy-800" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
                  API Secret Key
                </label>
                <div className="flex w-full">
                  <input
                    id="auth2"
                    name="auth2"
                    type="text"
                    value={apiSecret}
                    onKeyDown={(e) => e.preventDefault()}
                    className="custom-readonly caret-transparent h-10 w-full cursor-default rounded-l-md border-y border-l border-gray-300 bg-gray-50 px-4 text-sm text-gray-700 shadow-sm outline-none"
                  />
                  <div className="flex items-center rounded-r-md border border-gray-300 bg-gray-100 px-1">
                    <Tooltip
                      title="You will only see the unmask API Secret once, immediately after it's generated."
                      arrow
                      placement="top"
                    >
                      <IconButton id="button-info-auth2" size="small">
                        <MdInfoOutline className="h-5 w-5 text-navy-800" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy API Secret" arrow placement="top">
                      <IconButton
                        id="button-copy-auth2"
                        size="small"
                        disabled={isCreating}
                        onClick={() => handleClickCopy(apiSecret)}
                      >
                        <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate API Secret" arrow placement="top">
                      <IconButton
                        id="button-generate-api-secret"
                        size="small"
                        disabled={isCreating}
                        onClick={generateAPISecret}
                      >
                        <TbRefresh className="h-5 w-5 text-navy-800" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 border-b border-gray-200 pb-8">
            <p className="text-md font-medium tracking-normal text-gray-700">
              Data Privacy
            </p>
            <label className="flex cursor-pointer items-center space-x-3">
              <input
                type="checkbox"
                checked={newAccount.is_masked === "yes"}
                onChange={(e) =>
                  setNewAccount({
                    ...newAccount,
                    is_masked: e.target.checked ? "yes" : "no",
                  })
                }
                disabled={isCreating}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-navy-700">
                Mask Account Data
              </span>
            </label>
          </div>

          <div className="md:text-right">
            <Button
              onClick={() => navigate(isMerchant ? `/${userRole}/subaccounts` : `/${userRole}/merchants/subaccounts`)}
              isDisabled={isCreating}
              className="mr-4 rounded-md border border-gray-300 bg-white px-8 py-2 text-sm font-semibold tracking-tight text-gray-700 shadow-sm transition hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 md:px-10"
            >
              Back
            </Button>
            <Button
              onClick={() => handleSubmit()}
              isDisabled={!isFormValid || isCreating}
              isLoading={isCreating}
              loadingText="Creating..."
              className="btn-primary relative overflow-hidden rounded-md px-8 py-2.5 font-semibold text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 md:px-10"
            >
              <span className="relative z-0 tracking-tight">
                Create Account
              </span>
              <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
            </Button>
          </div>
        </div>
      </form>

      {/* Merchant Selection Modal */}
      {isMerchantModalOpen && (
        <MerchantListModal
          isOpen={isMerchantModalOpen}
          onClose={() => setIsMerchantModalOpen(false)}
          onSelect={handleMerchantSelect}
          entityType="MERC#"
        />
      )}

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default CreateAccount;
